import React, { createContext, useContext, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import VariantModal from "@/modules/storefront/components/VariantModal";
import { track } from "@/lib/tracker";
import { trackEvent } from "@/hooks/useTracking";

const QuickAddContext = createContext(null);

export function QuickAddProvider({ children }) {
  const { addItem, openDrawer } = useCart();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [show, setShow] = useState(false);
  const [adding, setAdding] = useState(false);
  const [loadingSlug, setLoadingSlug] = useState(null);

  const openQuickAdd = useCallback(async (slug) => {
    if (!slug) return;
    setLoadingSlug(slug);
    try {
      const res = await api.get(`/products/${slug}`);
      const p = res.data;
      setProduct(p);
      setQuantity(1);
      // Auto-select first in-stock variant
      const firstInStock = (p.variants || []).find(v => {
        const inv = v.warehouse_inventory;
        return !inv || typeof inv.available !== "number" || inv.available > 0;
      });
      // If product has only 1 variant and it's in stock, add directly — no modal
      if (p.variants?.length === 1 && firstInStock) {
        try {
          setAdding(true);
          await addItem(p.id, firstInStock.id, 1);
          toast.success("Added to cart!");
          openDrawer();
          track("add_to_cart", { product_id: p.id, product_name: p.title, variant_id: firstInStock.id, quantity: 1 });
          trackEvent("add_to_cart", {
            product_id: p.id, product_name: p.title,
            sku: firstInStock.sku || "",
            price: firstInStock.sale_price ?? firstInStock.price_inr ?? 0,
            quantity: 1,
          });
        } catch { toast.error("Failed to add to cart"); }
        finally { setAdding(false); }
      } else {
        setSelectedVariant(firstInStock || p.variants?.[0] || null);
        setShow(true);
      }
    } catch {
      toast.error("Could not load product");
    } finally {
      setLoadingSlug(null);
    }
  }, [addItem, openDrawer]);

  const handleAddToCart = async () => {
    if (!selectedVariant || !product) return;
    const inv = selectedVariant.warehouse_inventory;
    const avail = inv && typeof inv.available === "number" ? inv.available : null;
    if (avail !== null && avail <= 0) { toast.error("This item is out of stock"); return; }
    if (avail !== null && quantity > avail) { toast.error("Quantity exceeds available stock"); return; }
    setAdding(true);
    try {
      await addItem(product.id, selectedVariant.id, quantity);
      toast.success(`Added ${quantity} item(s) to cart!`);
      setShow(false);
      openDrawer();
      track("add_to_cart", { product_id: product.id, product_name: product.title, variant_id: selectedVariant.id, quantity });
      trackEvent("add_to_cart", {
        product_id: product.id, product_name: product.title,
        sku: selectedVariant.sku || "",
        price: selectedVariant.sale_price ?? selectedVariant.price_inr ?? 0,
        quantity,
      });
      setShow(false);
    } catch { toast.error("Failed to add to cart"); }
    finally { setAdding(false); }
  };
  return (
    <QuickAddContext.Provider value={{ openQuickAdd, loadingSlug }}>
      {children}
      <VariantModal
        show={show}
        onClose={() => setShow(false)}
        product={product}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        quantity={quantity}
        setQuantity={setQuantity}
        adding={adding}
        onAddToCart={handleAddToCart}
      />
    </QuickAddContext.Provider>
  );
}

export const useQuickAdd = () => useContext(QuickAddContext) || { openQuickAdd: () => {}, loadingSlug: null };
